import React, { useMemo, useState } from 'react';
import { Pagination, Spin } from 'antd';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { FireOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useLoadHistoryQuery } from '../../store/api';

const PAGE_SIZE = 5;
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

/** fecha_registro es hora de Colombia, sin zona en el texto. */
const cargaMs = (d) => {
  if (d?.fecha) {
    const raw = String(d.fecha).trim().replace(' ', 'T');
    if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(raw)) {
      return dayjs(`${raw}-05:00`).valueOf();
    }
    return dayjs(raw).valueOf();
  }
  return d?.timestamp;
};

const relativeLoadLabel = (ratio) => `${Math.round(ratio * 100)}% rel.`;
const relativeLoadColor = (ratio) => {
  if (ratio >= 1.25) return '#dc3545';
  if (ratio >= 1) return '#fd7e14';
  if (ratio >= 0.7) return '#eab308';
  return '#10b759';
};

const LoadSparkline = ({ serverId, cores }) => {
  const { data, isFetching, isError } = useLoadHistoryQuery(
    { dias: 1, agrupacion: 'raw', id_servidor: serverId },
    { skip: !serverId }
  );

  const points = useMemo(() => {
    const cutoff = Date.now() - SIX_HOURS_MS;
    const srv = data?.success ? (data.servidores || []).find((s) => String(s.id) === String(serverId)) : null;
    if (!srv?.datos?.length) return [];
    return srv.datos
      .map((d) => ({ x: cargaMs(d), y: d.carga }))
      .filter((p) => p.x > cutoff);
  }, [data, serverId]);

  if (isFetching) {
    return (
      <div style={{ padding: '16px 0', textAlign: 'center' }}>
        <Spin size="small" /> <span style={{ color: '#8392a5', fontSize: 12, marginLeft: 8 }}>Carga 6h…</span>
      </div>
    );
  }

  if (isError || points.length === 0) {
    return (
      <p style={{ margin: '8px 0 4px', color: '#8392a5', fontSize: 12 }}>
        Sin historial de carga en las últimas 6 horas.
      </p>
    );
  }

  const series = [{ name: 'Load 1m', data: points }];
  const maxY = Math.max(...points.map((p) => p.y), cores || 1);

  const options = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      sparkline: { enabled: false },
      fontFamily: 'Inter, sans-serif',
      animations: { enabled: true, speed: 400 },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#fd7e14'],
    fill: {
      type: 'gradient',
      gradient: { opacityFrom: 0.35, opacityTo: 0.05 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: '#e5e9f2',
      padding: { left: 4, right: 8 },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        style: { fontSize: '10px', colors: '#8392a5' },
        datetimeFormatter: { hour: 'HH:mm' },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxY * 1.15 * 10) / 10,
      tickAmount: 3,
      labels: {
        style: { fontSize: '10px', colors: '#8392a5' },
        formatter: (v) => (v == null ? '' : Number(v).toFixed(1)),
      },
    },
    tooltip: {
      x: { format: 'HH:mm' },
      y: { formatter: (v) => (v == null ? '—' : Number(v).toFixed(2)) },
    },
    annotations: cores
      ? {
          yaxis: [
            {
              y: cores,
              borderColor: '#8392a5',
              strokeDashArray: 4,
              label: {
                text: `${cores} núcleos`,
                style: { fontSize: '10px', color: '#8392a5', background: '#fafbfc' },
              },
            },
          ],
        }
      : undefined,
  };

  return (
    <div className="yzen-load-accordion-chart">
      <div style={{ fontSize: 11, color: '#8392a5', marginBottom: 4 }}>Últimas 6 horas · load average 1m</div>
      <Chart type="area" height={120} options={options} series={series} />
    </div>
  );
};

const HighRelativeLoadCard = ({ servers = [] }) => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const pageData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return servers.slice(start, start + PAGE_SIZE);
  }, [servers, page]);

  const handleToggle = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="yzen-card">
      <h3 className="yzen-card-title"><FireOutlined /> Carga relativa alta</h3>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#8392a5' }}>
        Load average ÷ núcleos (100% = saturado). Solo servidores con sync reciente (10 min).
        Toca un servidor para ver las últimas 6 horas.
      </p>
      {servers.length === 0 ? (
        <p style={{ color: '#8392a5', fontSize: 13 }}>Sin datos recientes de carga.</p>
      ) : (
        <>
          {pageData.map((srv) => {
            const color = relativeLoadColor(srv._loadRatio);
            const open = expandedId === srv.id;
            return (
              <div key={srv.id} className={`yzen-load-row ${open ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="yzen-list-item yzen-load-row-btn"
                  onClick={() => handleToggle(srv.id)}
                  aria-expanded={open}
                >
                  <div className="yzen-list-icon">
                    <ThunderboltOutlined style={{ color }} />
                  </div>
                  <div className="yzen-list-body">
                    <div className="yzen-list-title">{srv.nombre}</div>
                    <div className="yzen-list-sub">
                      {srv.ip} · {srv._load1.toFixed(2)} / {srv._cores} núcleos
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="yzen-list-meta" style={{ color, fontWeight: 600 }}>
                      {relativeLoadLabel(srv._loadRatio)}
                    </div>
                    <div className="yzen-list-time">{srv.carga || '—'}</div>
                  </div>
                </button>
                {open && (
                  <div className="yzen-load-accordion-panel">
                    <LoadSparkline serverId={srv.id} cores={srv._cores} />
                  </div>
                )}
              </div>
            );
          })}
          {servers.length > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <Pagination
                size="small"
                current={page}
                pageSize={PAGE_SIZE}
                total={servers.length}
                onChange={(p) => {
                  setPage(p);
                  setExpandedId(null);
                }}
                showSizeChanger={false}
                showTotal={(total) => `${total} servidores`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HighRelativeLoadCard;
